import { useState } from 'react';
import { container, item } from '../../utils/animations';

import styles from './ContactUsForm.module.css';
import sharedStyles from '../../../models/css/sharedStyles.module.css';

import { AnimatePresence, motion } from 'framer-motion';
import emailjs from '@emailjs/browser';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

export const ContactUsForm = () => {
    const { t } = useTranslation();
    const [errors, setErrors] = useState<Record<string, string>>({});

    const validate = (values: Record<string, any>) => {
        const newErrors: Record<string, string> = {};

        if (!values.email) newErrors.email = t('contact.form.errors.email');
        if (!values.name) newErrors.name = t('contact.form.errors.name');
        if (!values.description) newErrors.description = t('contact.form.errors.description');

        return newErrors;
    };

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const form = e.currentTarget;
        const formData = new FormData(form);

        const values = {
            email: formData.get('email'),
            name: formData.get('name'),
            country: formData.get('country'),
            reason: formData.get('reason'),
            description: formData.get('description'),
        };

        const validationErrors = validate(values);

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setErrors({});

        emailjs
            .send(
                'service_5rcbh52',
                'template_prygeyr',
                {
                    email: values.email,
                    name: values.name,
                    message: values.description,
                    from_name: values.email,
                    reason: values.reason,
                    country: values.country,
                },
                {
                    publicKey: 'yYS0j8kuu-0KYHX4j',
                }
            )
            .then(
                () => {
                    form.reset();
                    toast.success(t('contact.form.toast.success'));
                },
                (error) => {
                    console.error('email error: ', error);
                    toast.error(
                        t('contact.form.toast.error')
                    );
                }
            );
    };

    return (
        <section className={styles.wrapper}>
            <motion.div
                className={styles.container}
                variants={container}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.2 }}
            >
                <motion.h1 className={sharedStyles.mainTitle} variants={item}>
                    {t('contact.form.title')}
                </motion.h1>

                <form className={styles.form} onSubmit={handleSubmit}>
                    {/* EMAIL */}
                    <motion.div className={styles.field} variants={item}>
                        <label>{t('contact.form.title')}</label>
                        <input
                            type="email"
                            name="email"
                            className={errors.email ? styles.inputError : ''}
                        />
                        <AnimatePresence>
                            <motion.span
                                className={styles.errorText}
                                initial={{ opacity: 0, y: -6 }}
                                animate={{ opacity: errors.email ? 1 : 0 }}
                                exit={{ opacity: 0, y: -6 }}
                                transition={{ duration: 0.2 }}
                            >
                                {errors.email}
                            </motion.span>
                        </AnimatePresence>
                    </motion.div>

                    {/* NAME */}
                    <motion.div className={styles.field} variants={item}>
                        <label>{t('contact.form.name')}</label>
                        <input
                            type="text"
                            name="name"
                            className={errors.name ? styles.inputError : ''}
                        />
                        <AnimatePresence>
                            <motion.span
                                className={styles.errorText}
                                initial={{ opacity: 0, y: -6 }}
                                animate={{ opacity: errors.email ? 1 : 0 }}
                                exit={{ opacity: 0, y: -6 }}
                                transition={{ duration: 0.2 }}
                            >
                                {errors.name}
                            </motion.span>
                        </AnimatePresence>
                    </motion.div>

                    {/* COUNTRY */}
                    <motion.div className={styles.field} variants={item}>
                        <label>{t('contact.form.country')}</label>
                        <input type="text" name="country" />
                        <AnimatePresence>
                            <motion.span
                                className={styles.errorText}
                                initial={{ opacity: 0, y: -6 }}
                                animate={{ opacity: errors.email ? 1 : 0 }}
                                exit={{ opacity: 0, y: -6 }}
                                transition={{ duration: 0.2 }}
                            >
                                {errors.country}
                            </motion.span>
                        </AnimatePresence>
                    </motion.div>

                    {/* REASON */}
                    <motion.div className={styles.field} variants={item}>
                        <label>{t('contact.form.contactReason')}</label>
                        <input type="text" name="reason" />
                        <AnimatePresence>
                            <motion.span
                                className={styles.errorText}
                                initial={{ opacity: 0, y: -6 }}
                                animate={{ opacity: errors.email ? 1 : 0 }}
                                exit={{ opacity: 0, y: -6 }}
                                transition={{ duration: 0.2 }}
                            >
                                {errors.reason}
                            </motion.span>
                        </AnimatePresence>
                    </motion.div>

                    {/* DESCRIPTION */}
                    <motion.div className={styles.field} variants={item}>
                        <label>{t('contact.form.description')}</label>

                        <span className={styles.helperText}>
                            {t('contact.form.descriptionHelperText')}
                        </span>

                        <textarea
                            name="description"
                            rows={8}
                            className={errors.description ? styles.textareaError : ''}
                        />

                        <AnimatePresence>
                            <motion.span
                                className={styles.errorText}
                                initial={{ opacity: 0, y: -6 }}
                                animate={{ opacity: errors.email ? 1 : 0 }}
                                exit={{ opacity: 0, y: -6 }}
                                transition={{ duration: 0.2 }}
                            >
                                {errors.description}
                            </motion.span>
                        </AnimatePresence>
                    </motion.div>

                    <motion.button
                        variants={item}
                        type="submit"
                        className={styles.submitButton}
                    >
                        {t('contact.form.send')}
                    </motion.button>
                </form>
            </motion.div>
        </section>
    );
};